import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { getCategoryBySlug } from "@/mocks/categories";
import { formatDate, formatNumber, tintMap } from "@/lib/content";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "wide" | "compact" | "minimal";
  rank?: number;
}

export default function ArticleCard({ article, variant = "default", rank }: ArticleCardProps) {
  const category = getCategoryBySlug(article.categorySlug);
  const tint = tintMap[category?.tint ?? "primary"];
  const to = `/article/${article.id}`;

  if (variant === "minimal") {
    return (
      <article className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-b-0">
        <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-sm font-semibold text-foreground-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
          {typeof rank === "number" ? rank : <i className="ri-arrow-right-up-line"></i>}
        </span>
        <div className="min-w-0">
          <h3 className="text-sm leading-relaxed text-foreground-900 group-hover:text-primary-700 transition-colors">
            <Link to={to} className="line-clamp-2">{article.title}</Link>
          </h3>
          <p className="mt-1 text-xs text-foreground-500 flex items-center gap-3">
            <span>{category?.shortName}</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatNumber(article.views)}</span>
          </p>
        </div>
      </article>
    );
  }

  if (variant === "compact") {
    return (
      <article className="group flex gap-3.5">
        <Link to={to} className="shrink-0 w-24 h-20 rounded-md overflow-hidden bg-background-200">
          <img src={article.cover} alt={article.coverAlt} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        </Link>
        <div className="min-w-0 flex flex-col">
          <span className={`text-[11px] ${tint.text}`}>{category?.name}</span>
          <h3 className="mt-0.5 text-sm leading-snug text-foreground-900 group-hover:text-primary-700 transition-colors">
            <Link to={to} className="line-clamp-2">{article.title}</Link>
          </h3>
          <p className="mt-auto text-xs text-foreground-500">{formatDate(article.date)}</p>
        </div>
      </article>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group grid grid-cols-1 sm:grid-cols-[220px_1fr] lg:grid-cols-[260px_1fr] gap-4 md:gap-6">
        <Link to={to} className="block w-full aspect-[16/10] rounded-lg overflow-hidden bg-background-200">
          <img src={article.cover} alt={article.coverAlt} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        </Link>
        <div className="flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${tint.chipBg} ${tint.chipText}`}>
              {category?.name}
            </span>
            {article.hot && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-primary-100 text-primary-800">
                <i className="ri-fire-line"></i>热
              </span>
            )}
          </div>
          <h3 className="mt-2.5 text-lg md:text-xl font-semibold leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors">
            <Link to={to}>{article.title}</Link>
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">{article.excerpt}</p>
          <div className="mt-auto pt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>{formatDate(article.date)}</span>
            <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.readTime}</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatNumber(article.views)}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col h-full">
      <Link to={to} className="block w-full aspect-[16/10] rounded-lg overflow-hidden bg-background-200">
        <img src={article.cover} alt={article.coverAlt} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
      </Link>
      <div className="flex flex-col flex-1 pt-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${tint.chipBg} ${tint.chipText}`}>
            {category?.name}
          </span>
          {article.hot && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-primary-100 text-primary-800">
              <i className="ri-fire-line"></i>热
            </span>
          )}
        </div>
        <h3 className="mt-2.5 text-base md:text-lg font-semibold leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors">
          <Link to={to} className="line-clamp-2">{article.title}</Link>
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">{article.excerpt}</p>
        <div className="mt-auto pt-4 flex items-center gap-3 text-xs text-foreground-500">
          <span>{formatDate(article.date)}</span>
          <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatNumber(article.views)}</span>
        </div>
      </div>
    </article>
  );
}